import { Link, useParams } from "react-router-dom";
import { listPractices, listStandards, listTools } from "@/api/endpoints";
import { LinkedFromCatalog } from "@/components/LinkedFromCatalog";
import { BrowseLoading, BrowsePage } from "@/components/BrowsePage";
import { PillButton } from "@/components/ui/PillButton";
import type { Searchable } from "@/lib/search";
import { useAsync } from "@/lib/useAsync";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import type { Practice, Standard, Tool } from "@/api/types";
import { FitsReminder } from "@/components/HowItFitsTogether";
import { CatalogCard } from "@/components/CatalogCard";
import { PracticeDetail } from "@/components/PracticeDetail";
import { RingBadge } from "@/components/ui/RingBadge";
import {
  FLAG_LABELS,
  attentionCounts,
  attentionOptions,
  catalogSearchable,
  passesAttention,
  type AttentionFilter,
  type CatalogEntry,
} from "@/lib/toolSearch";
import { updatedLabel } from "@/lib/time";

/** Badge color per status: only `enforced` reads as settled; `stated` and `aspirational`
 * are flagged, since neither is yet something a team can be held to. */
const STATUS_TONE = { enforced: "accent", documented: "neutral", stated: "warn", aspirational: "warn" } as const;

/** One entry on the Standards tab: a standard, or a radar practice. */
type Entry = { type: "standard"; item: Standard } | Extract<CatalogEntry, { type: "practice" }>;

/** Everything a search can match on one standard, and the fields its pills search:
 * `status`, `tier` (0–3, or "none"), `applies` (always / surface),
 * `enforcement`, and `ring` (from the radar practice linked as the same subject, or
 * "unrated"). The text also covers the tools and practices confirmed as meeting it. */
function standardSearchable(s: Standard, tools: Tool[], practices: Practice[]): Searchable {
  const meeting = [
    ...tools.filter((t) => t.satisfies_standards.includes(s.id)).map((t) => t.name),
    ...practices.filter((p) => p.related_standards.includes(s.id)).map((p) => p.name),
  ];
  const text = [
    String(s.display_number), s.name, s.id, s.area, s.status, s.tier === null ? "" : `tier ${s.tier}`,
    s.what, s.why ?? "", s.how_to_measure ?? "", s.summary, s.covers, ...s.surfaces, ...s.aliases, ...s.key_points,
    ...meeting, s.radar?.name ?? "",
  ].join(" ").toLowerCase(); // prettier-ignore
  return {
    text,
    fields: {
      status: [s.status],
      tier: [s.tier === null ? "none" : String(s.tier)],
      applies: [s.applies],
      enforcement: [s.enforcement ?? "none"],
      ring: [s.radar?.ring ?? "unrated"],
    },
  };
}

/** Browse what to *do* in the shared layout (components/BrowsePage.tsx): the engineering
 * standards — what's required — in display-number order (Tier 0 tiles carry a
 * warning-coloured left border; those are non-negotiable), then the Tech Radar's practices —
 * recognised ways to meet them — alphabetically. A practice that is a standard's same subject
 * is linked both ways, so the standard shows its radar ring. Details are at `/standards/<id>`
 * and `/practices/<id>`. The pulldown narrows to standards, practices, or practices needing
 * attention (their flags, as on the Tools tab). */
export function StandardsPage() {
  const state = useAsync(() => Promise.all([listStandards(), listTools(), listPractices()]), []);
  const { standardId, practiceId } = useParams();

  if (state.status !== "ready")
    return (
      <BrowseLoading
        title="Engineering Standards and Practices"
        intro={<FitsReminder />}
        noun="standards"
        error={state.status === "error" ? state.message : undefined}
      />
    );

  const [stds, { tools }, { practices }] = state.data;
  const standardNames = new Map(stds.standards.map((s) => [s.id, s.name]));
  const practiceEntries = practices
    .map((item) => ({ type: "practice" as const, item }))
    .sort((a, b) => a.item.name.localeCompare(b.item.name));
  const entries: Entry[] = [
    ...[...stds.standards]
      .sort((a, b) => a.display_number - b.display_number)
      .map((item) => ({ type: "standard" as const, item })),
    ...practiceEntries,
  ];
  const counts = attentionCounts(practiceEntries);
  const noNames = new Map<string, string>();

  return (
    <BrowsePage
      title="Engineering Standards and Practices"
      meta={
        <>
          <span title={updatedLabel(stds.last_updated).title}>{updatedLabel(stds.last_updated).text}</span>
          {" · "}
          {stds.standards.length} standards, {practices.length} radar practices
        </>
      }
      intro={<FitsReminder />}
      items={entries}
      itemKey={(e) => `${e.type}:${e.item.id}`}
      itemPath={(e) => `/${e.type === "standard" ? "standards" : "practices"}/${encodeURIComponent(e.item.id)}`}
      selectedKey={standardId ? `standard:${standardId}` : practiceId ? `practice:${practiceId}` : null}
      basePath="/standards"
      searchable={(e) => {
        if (e.type === "standard") return standardSearchable(e.item, tools, practices);
        // A practice's `kind` is always "practice" — one value, and the pulldown already splits
        // standards from practices — so only the fields with something to choose between stay.
        const { fields, ...rest } = catalogSearchable(e, standardNames, noNames);
        return { ...rest, fields: Object.fromEntries(Object.entries(fields).filter(([f]) => f !== "kind")) };
      }}
      hintLabels={{ flag: FLAG_LABELS }}
      storageKey="standards"
      noun="standards"
      filter={{
        options: [
          { value: "", label: "Standards and practices" },
          { value: "only:standard", label: `Standards only (${stds.standards.length})` },
          { value: "only:practice", label: `Radar practices only (${practices.length})` },
          ...attentionOptions(counts)
            .slice(1)
            .map((o) => ({ ...o, label: `Practices — ${o.label}` })),
        ],
        passes: (e, value) =>
          value === "only:standard"
            ? e.type === "standard"
            : value === "only:practice"
              ? e.type === "practice"
              : e.type === "practice" && passesAttention(e, value as AttentionFilter),
      }}
      card={(e) =>
        e.type === "standard" ? (
          <StandardCard standard={e.item} />
        ) : (
          <CatalogCard entry={e} standardNames={standardNames} />
        )
      }
      row={(e) => (
        <span className="flex items-center gap-2">
          <span className="flex-1 truncate">{e.item.name}</span>
          {e.type === "practice" && <span className="text-xs text-text/40">practice</span>}
        </span>
      )}
      detail={(e, onSearch) =>
        e.type === "standard" ? (
          <StandardDetail standard={e.item} tools={tools} practices={practices} onSearch={onSearch} />
        ) : (
          <PracticeDetail practice={e.item} standards={stds.standards} onChanged={state.reload} onSearch={onSearch} />
        )
      }
    />
  );
}

/** A standard's tile: name, status, the radar ring when a practice is its same subject, a
 * trimmed summary and when it applies. */
function StandardCard({ standard: s }: { standard: Standard }) {
  return (
    <Card
      className={`h-full cursor-pointer hover:border-accent-line ${
        s.tier === 0 ? "rounded-l-none border-l-[3px] border-l-warn" : ""
      }`}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="font-medium">{s.name}</h3>
        <div className="flex shrink-0 gap-1">
          {s.radar?.ring && <RingBadge ring={s.radar.ring} />}
          <Badge tone={STATUS_TONE[s.status]}>{s.status}</Badge>
        </div>
      </div>
      <p className="text-sm text-text/70">{s.summary}</p>
      <p className="mt-2 text-xs text-text/50">{s.covers}</p>
    </Card>
  );
}

/** One standard in full: area and tier, what it requires, why, how it's measured, its
 * resolve-within window, any registry note, the tools and practices that meet it (and any
 * drafted links to it), and its references. Read-only here. */
function StandardDetail({
  standard: s,
  tools,
  practices,
  onSearch,
}: {
  standard: Standard;
  tools: Tool[];
  practices: Practice[];
  onSearch: (field: string, value: string, toggle?: boolean) => void;
}) {
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-[family-name:--font-display] text-xl">{s.name}</h3>
          <p className="text-xs text-text/50">
            {s.area}
            {s.tier !== null && ` · Tier ${s.tier}`}
          </p>
        </div>
        <PillButton
          onClick={(toggle) => onSearch("status", s.status, toggle)}
          title={`Show every ${s.status} standard`}
        >
          <Badge tone={STATUS_TONE[s.status]}>{s.status}</Badge>
        </PillButton>
      </div>
      <p className="leading-relaxed">{s.what}</p>
      {s.radar && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-line p-2.5 text-sm">
          <span className="text-xs font-semibold tracking-wide text-text/60 uppercase">On the Tech Radar</span>
          <Link to={`/practices/${encodeURIComponent(s.radar.practice_id)}`} className="text-accent underline">
            {s.radar.name}
          </Link>
          {s.radar.ring && (
            <PillButton
              onClick={(toggle) => onSearch("ring", s.radar!.ring!, toggle)}
              title={`Show everything rated ${s.radar.ring}`}
            >
              <RingBadge ring={s.radar.ring} />
            </PillButton>
          )}
          {s.radar.radar_key && (
            <a
              href={`https://guild-education.atlassian.net/browse/${s.radar.radar_key}`}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-xs text-accent underline"
            >
              {s.radar.radar_key}
            </a>
          )}
          {s.radar.radar_url && (
            <a href={s.radar.radar_url} target="_blank" rel="noreferrer" className="text-accent underline">
              Radar write-up
            </a>
          )}
        </div>
      )}
      {s.why && (
        <p className="text-sm text-text/70">
          <strong>Why:</strong> {s.why}
        </p>
      )}
      {s.how_to_measure && (
        <p className="text-sm text-text/70">
          <strong>How to measure:</strong> {s.how_to_measure}
        </p>
      )}
      {s.resolve_within && (
        <p className="text-sm text-text/70">
          <strong>Resolve within:</strong> {s.resolve_within}
        </p>
      )}
      {s.note && (
        <div className="rounded border border-warn-line bg-warn-bg p-2.5 text-sm">
          <strong className="text-warn">Note.</strong> {s.note}
        </div>
      )}
      <LinkedFromCatalog id={s.id} link="standard" tools={tools} practices={practices} />
      {s.references.length > 0 && (
        <div>
          <h4 className="mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase">References</h4>
          <ul className="flex flex-col gap-1 text-sm">
            {s.references.map((r) => (
              <li key={r.url}>
                <a href={r.url} target="_blank" rel="noreferrer" className="text-accent underline">
                  {r.title}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
